import React from "react";
import { AlertTriangle, TrendingUp, Shield, DollarSign } from "lucide-react";

interface RiskManagementSummaryProps {
  result: any;
}

export const RiskManagementSummary: React.FC<RiskManagementSummaryProps> = ({ result }) => {
  if (!result) return null;

  const getRiskLevel = (dailyRisk: number) => {
    if (dailyRisk <= 1) return { text: "Conservative", color: "text-green-400" };
    if (dailyRisk <= 2) return { text: "Moderate", color: "text-yellow-400" };
    return { text: "Aggressive", color: "text-red-400" };
  };

  const riskLevel = getRiskLevel(parseFloat(result.dailyRiskPercent));

  return (
    <div className="bg-[#2a2a2a] rounded-2xl shadow-lg p-4 md:p-6 mt-6">
      <h2 className="text-base md:text-lg font-bold text-neutral-100 mb-4">Risk Management Summary</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-neutral-800/50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <Shield className="h-5 w-5 text-yellow-400" />
            <h3 className="text-sm md:text-base font-semibold text-neutral-300">Risk Profile</h3>
          </div>
          <div className="space-y-2">
            <p className="text-sm md:text-base text-neutral-300">
              Daily Risk: <span className={riskLevel.color}>{result.dailyRiskPercent}%</span>
            </p>
            <p className="text-sm md:text-base text-neutral-300">
              Risk Level: <span className={riskLevel.color}>{riskLevel.text}</span>
            </p>
            <p className="text-sm md:text-base text-neutral-300">
              Max Drawdown: <span className="text-yellow-400">{result.maxDrawdownPercent}%</span>
            </p>
          </div>
        </div>

        <div className="bg-neutral-800/50 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-5 w-5 text-yellow-400" />
            <h3 className="text-sm md:text-base font-semibold text-neutral-300">Profit Potential</h3>
          </div>
          <div className="space-y-2">
            <p className="text-sm md:text-base text-neutral-300">
              Risk/Reward: <span className="text-yellow-400">{typeof result.riskToRewardRatio === 'number' ? result.riskToRewardRatio.toFixed(2) : "-"}</span>
            </p>
            <p className="text-sm md:text-base text-neutral-300">
              Potential Profit: <span className="text-yellow-400">${typeof result.potentialProfit === 'number' ? result.potentialProfit.toFixed(2) : "-"}</span>
            </p>
            <p className="text-sm md:text-base text-neutral-300">
              Required Margin: <span className="text-yellow-400">${result.marginRequired || "-"}</span>
            </p>
          </div>
        </div>
      </div>

      {(result.marginWarning || result.drawdownWarning) && (
        <div className="mt-4 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              {result.marginWarning && (
                <p className="text-sm md:text-base text-red-400">
                  Your planned lot size may exceed available margin. Consider reducing position size.
                </p>
              )}
              {result.drawdownWarning && (
                <p className="text-sm md:text-base text-red-400">
                  You're approaching your maximum drawdown limit. Consider reducing risk exposure.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="mt-4 bg-neutral-800/50 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-3">
          <DollarSign className="h-5 w-5 text-yellow-400" />
          <h3 className="text-sm md:text-base font-semibold text-neutral-300">Risk Management Tips</h3>
        </div>
        <ul className="space-y-2 text-sm md:text-base text-neutral-300">
          <li>• Keep daily risk below 2% of your account</li>
          <li>• Maintain a minimum 1:2 risk-to-reward ratio</li>
          <li>• Never risk more than 5% of your account on a single trade</li>
          <li>• Consider using stop-loss orders on all positions</li>
        </ul>
      </div>
    </div>
  );
};

export default RiskManagementSummary; 